/**
 * Hook di gating UI basato sui privilegi RBAC *effettivi* (editabili da Admin).
 *
 * A differenza di {@link hasPermission} — che deriva i permessi dallo specchio
 * statico `ROLE_PERMISSIONS` — questo hook legge la mappa ruolo→privilegi
 * runtime (`GET /admin/role-permissions`, gated VIEW_PORTFOLIO, leggibile da
 * ogni ruolo autenticato), così le concessioni fatte nella sezione Admin
 * "Privilegi RBAC per ruolo" si riflettono subito nella UI.
 *
 * La fetch effettiva NON è fatta qui: l'avvia una sola volta il Layout
 * autenticato (vedi `app/Layout.tsx`) sotto la queryKey condivisa
 * `['admin','role-permissions']`. Questo hook si limita a *leggere* quella
 * cache (con sottoscrizione, così si ri-renderizza quando la mappa arriva o
 * cambia) senza generare richieste proprie — evita fetch a cascata nei
 * componenti e mantiene i test di pagina isolati indipendenti dalla rete.
 *
 * ATTENZIONE: resta solo gating UI. L'autorizzazione effettiva è applicata
 * server-side da @tenaciousc/auth (che legge la stessa tabella role_permissions).
 *
 * Fail-safe: finché la mappa effettiva non è in cache (o se la fetch fallisce)
 * ricade sullo specchio statico (`rolesToPermissions`), cioè il comportamento
 * conservativo precedente.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext.js';
import { rolesToPermissions } from './roles.js';
import type { TcPermission } from './types.js';
import { fetchRolePermissions } from '../features/admin/role-permissions-api.js';

export interface PermissionsApi {
  /** True se l'utente corrente possiede il privilegio (mappa effettiva o fallback statico). */
  can: (perm: TcPermission) => boolean;
}

export function usePermissions(): PermissionsApi {
  const { claims } = useAuth();

  // Solo lettura della cache condivisa: `enabled: false` → nessuna fetch propria,
  // ma resta sottoscritto agli aggiornamenti della stessa queryKey (la fetch la
  // fa il Layout). queryKey == adminKeys.rolePermissions().
  const { data } = useQuery({
    queryKey: ['admin', 'role-permissions'],
    queryFn: fetchRolePermissions,
    enabled: false,
  });

  const effective = useMemo<Set<TcPermission>>(() => {
    const roles = claims?.roles ?? [];
    // Bypass hardwired: admin ha SEMPRE tutti i privilegi, indipendentemente dalla
    // mappa server. Specchio del bypass code-level in packages/auth/src/roles.ts.
    if (roles.includes('admin')) return rolesToPermissions(roles);
    // Fallback fail-safe allo specchio statico finché la mappa non è in cache.
    if (!data) return rolesToPermissions(roles);
    const set = new Set<TcPermission>();
    for (const role of roles) {
      for (const perm of data[role] ?? []) set.add(perm);
    }
    return set;
  }, [data, claims]);

  return {
    can: (perm: TcPermission) => effective.has(perm),
  };
}
